import React, { useEffect } from "react";
import { Link } from "react-router-dom";

// ============================================================================
// SEO METADATA
// ============================================================================
import BLOG_SEO from "@/data/blogSeo.json";
import { blogOgImage } from "@/components/RouteSeo";
const PAGE_URL = "https://mindbodyspirithygienetools.com/blog/you-feed-the-baby";
const PAGE_TITLE = BLOG_SEO.legacy["you-feed-the-baby"].title;
const PAGE_DESC = BLOG_SEO.legacy["you-feed-the-baby"].description;
const PAGE_IMAGE = blogOgImage("you-feed-the-baby");
const PUBLISH_DATE = "2026-06-11";

const buildSchemas = () => [
  {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": PAGE_URL,
    headline: "You Feed the Baby. Now Feed Yourself.",
    description: PAGE_DESC,
    url: PAGE_URL,
    datePublished: PUBLISH_DATE,
    dateModified: PUBLISH_DATE,
    image: PAGE_IMAGE,
    author: {
      "@type": "Person",
      name: "Temple Franklin",
      url: "https://mindbodyspirithygienetools.com/about",
    },
    publisher: {
      "@type": "Organization",
      name: "Mind Body Spirit HYGIENE™ Tools",
      logo: { "@type": "ImageObject", url: "https://mindbodyspirithygienetools.com/logo512.png" },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": PAGE_URL },
    keywords:
      "body hygiene, self-care, daily check-in, nourishment, hydration, rest, mother burnout, mom self-care, Temple Franklin, Mind Body Spirit HYGIENE, blood sugar mood, protein for mood",
    articleSection: "The Body",
    about: [
      { "@type": "Thing", name: "Self-care for women" },
      { "@type": "Thing", name: "Nutrition and mood" },
      { "@type": "Thing", name: "Daily wellness ritual" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://mindbodyspirithygienetools.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://mindbodyspirithygienetools.com/blog" },
      { "@type": "ListItem", position: 3, name: "You Feed the Baby. Now Feed Yourself.", item: PAGE_URL },
    ],
  },
];

import BlogCalendarOptin from "@/components/BlogCalendarOptin";

const BlogYouFeedTheBaby = () => {
  useEffect(() => {
    document.title = PAGE_TITLE;

    const upsert = (key, attr, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    upsert("description", "name", PAGE_DESC);
    upsert(
      "keywords",
      "name",
      "body hygiene, self-care, daily check-in, nourishment, hydration, rest, mom self-care, Temple Franklin, Mind Body Spirit HYGIENE, blood sugar mood"
    );
    upsert("og:type", "property", "article");
    upsert("og:title", "property", PAGE_TITLE);
    upsert("og:description", "property", PAGE_DESC);
    upsert("og:url", "property", PAGE_URL);
    upsert("og:image", "property", PAGE_IMAGE);
    upsert("og:site_name", "property", "Mind Body Spirit HYGIENE™ Tools");
    upsert("article:author", "property", "Temple Franklin");
    upsert("article:published_time", "property", PUBLISH_DATE);
    upsert("article:section", "property", "The Body");
    upsert("twitter:card", "name", "summary_large_image");
    upsert("twitter:title", "name", PAGE_TITLE);
    upsert("twitter:description", "name", PAGE_DESC);
    upsert("twitter:image", "name", PAGE_IMAGE);

    // Canonical (set or replace)
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", PAGE_URL);

    // JSON-LD schemas
    const schemas = buildSchemas();
    schemas.forEach((schema, i) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.blogBodySeo = "1";
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-blog-body-seo="1"]').forEach((s) => s.remove());
    };
  }, []);

  return (
    <article className="min-h-screen bg-[#efeae7]" data-testid="blog-you-feed-the-baby-page">
      {/* HERO */}
      <header className="pt-32 pb-12 px-6 text-center" data-testid="blog-hero">
        <div className="max-w-3xl mx-auto">
          <p
            className="font-sans text-[11px] tracking-[0.42em] uppercase text-[#068fa2] font-semibold mb-6"
            data-testid="blog-eyebrow"
          >
            MIND BODY SPIRIT HYGIENE™ · The Body
          </p>
          <h1
            className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter text-[#042F2E] leading-[1.05] mb-8"
            data-testid="blog-headline"
          >
            You Feed the Baby.<br />
            <span className="italic text-[#068fa2]">Now Feed Yourself.</span>
          </h1>
          <p
            className="font-serif italic text-lg md:text-xl text-[#1E2A29] max-w-xl mx-auto leading-relaxed"
            data-testid="blog-subhead"
          >
            Body hygiene — the daily check-in you&rsquo;d never skip for someone you love.
          </p>

          <div className="mt-12 mx-auto flex items-center justify-center gap-3" data-testid="blog-meta">
            <div className="h-px w-12 bg-[#d4af6a]/60" />
            <span className="text-[11px] tracking-[0.32em] uppercase text-[#5F7370] font-medium">
              4 min read
            </span>
            <div className="h-px w-12 bg-[#d4af6a]/60" />
          </div>
        </div>
      </header>

      {/* BODY */}
      <section className="px-6 pb-20" data-testid="blog-body">
        <div className="max-w-[680px] mx-auto font-serif text-[1.18rem] md:text-[1.22rem] leading-[1.85] text-[#042F2E]">

          <p className="mb-7">
            Hello Lovely,
          </p>

          <p className="mb-7">
            Think about how you care for a newborn. You feed her before she even cries. You keep her watered, warm, washed, and rested. You learn her rhythms — when she eats, when she sleeps, when she needs changing. Nobody calls that &ldquo;self-care.&rdquo; It&rsquo;s just <em>care.</em> It&rsquo;s <em>hygiene.</em> It&rsquo;s how a small body stays well.
          </p>

          <p className="mb-7">
            Now — when did you stop doing that for yourself?
          </p>

          <p className="mb-7">
            This is the part of Mind Body Spirit HYGIENE&trade; we don&rsquo;t talk about enough: <em>the Body.</em>
          </p>

          <p className="mb-7">
            Because here&rsquo;s the truth, dear one — you cannot show up rested, calm, or compassionate on an empty tank. When you skip meals and run on coffee and willpower, your body isn&rsquo;t being &ldquo;strong.&rdquo; It&rsquo;s being neglected. And it will tell on you — in your mood, your patience, your fog, even your breath.
          </p>

          <p className="mb-7">
            So <strong className="font-semibold">let&rsquo;s bring the same tenderness you&rsquo;d give a baby home to yourself.</strong>
          </p>

          {/* Section divider */}
          <div className="my-16 flex items-center justify-center gap-4" aria-hidden="true">
            <div className="h-px w-16 bg-[#d4af6a]/40" />
            <span className="text-[#d4af6a]">✦</span>
            <div className="h-px w-16 bg-[#d4af6a]/40" />
          </div>

          <h2 className="font-serif italic text-3xl md:text-4xl text-[#042F2E] text-center leading-tight mb-10" data-testid="section-five">
            The 5 You&rsquo;d Never Skip
          </h2>

          <p className="mb-9">
            This is your Body Check-In. Not a diet. Not a rulebook. Just five small kindnesses:
          </p>

          <ul className="mb-10 space-y-5 list-none pl-0" data-testid="five-checklist">
            <li className="flex items-start gap-4 p-5 bg-white/70 rounded-2xl border border-[#d4af6a]/30">
              <span className="font-sans text-[10px] tracking-[0.3em] uppercase font-bold text-[#d4af6a] mt-1.5 min-w-[60px]">Fed</span>
              <span>Have I eaten today — and did I get some <strong className="font-semibold">protein?</strong></span>
            </li>
            <li className="flex items-start gap-4 p-5 bg-white/70 rounded-2xl border border-[#d4af6a]/30">
              <span className="font-sans text-[10px] tracking-[0.3em] uppercase font-bold text-[#d4af6a] mt-1.5 min-w-[60px]">Watered</span>
              <span>Have I had water, not just caffeine?</span>
            </li>
            <li className="flex items-start gap-4 p-5 bg-white/70 rounded-2xl border border-[#d4af6a]/30">
              <span className="font-sans text-[10px] tracking-[0.3em] uppercase font-bold text-[#d4af6a] mt-1.5 min-w-[60px]">Rested</span>
              <span>Did I sleep enough, or can I give myself a pause?</span>
            </li>
            <li className="flex items-start gap-4 p-5 bg-white/70 rounded-2xl border border-[#d4af6a]/30">
              <span className="font-sans text-[10px] tracking-[0.3em] uppercase font-bold text-[#d4af6a] mt-1.5 min-w-[60px]">Moved</span>
              <span>Even a stretch, a short walk, a roll of the shoulders?</span>
            </li>
            <li className="flex items-start gap-4 p-5 bg-white/70 rounded-2xl border border-[#d4af6a]/30">
              <span className="font-sans text-[10px] tracking-[0.3em] uppercase font-bold text-[#d4af6a] mt-1.5 min-w-[60px]">Freshened</span>
              <span>Brushed, washed — the small dignities that say <em>I matter.</em></span>
            </li>
          </ul>

          <p className="mb-7 text-center font-serif italic text-xl text-[#068fa2]">
            Five questions. Sixty seconds. That&rsquo;s body hygiene.
          </p>

          {/* Section divider */}
          <div className="my-16 flex items-center justify-center gap-4" aria-hidden="true">
            <div className="h-px w-16 bg-[#d4af6a]/40" />
            <span className="text-[#d4af6a]">✦</span>
            <div className="h-px w-16 bg-[#d4af6a]/40" />
          </div>

          <h2 className="font-serif italic text-3xl md:text-4xl text-[#042F2E] text-center leading-tight mb-10" data-testid="section-science">
            The Science, Simply
          </h2>

          <p className="mb-7">
            None of this is willpower, Lovely. It&rsquo;s <strong className="font-semibold">biology</strong> — and it&rsquo;s kind of beautiful:
          </p>

          <p className="mb-7">
            When you go too long without eating, your blood sugar dips. A dipping brain gets foggy, anxious, and short-tempered — that&rsquo;s the very real thing we call <em>hangry.</em> Feed yourself, and the storm settles.
          </p>

          <p className="mb-7">
            <strong className="font-semibold">Protein first.</strong> You will notice by mid-morning.
          </p>

          <p className="mb-7">
            <strong className="font-semibold">Water matters</strong> because your brain is mostly water. Even a little dehydration reads as tired, headachy, and cranky.
          </p>

          <p className="mb-7">
            <strong className="font-semibold">Rest and movement matter</strong> too: sleep is when your body repairs, and even a short walk lifts your mood and steadies your energy.
          </p>

          <p className="mb-7">
            And yes — go too long on empty and your mouth dries, your breath turns, your focus crashes. Not a flaw. Just a body waving a little flag: <em>feed me.</em>
          </p>

          {/* Pull-quote */}
          <div
            className="my-14 py-10 px-6 text-center bg-[#042F2E] rounded-2xl"
            data-testid="pull-quote-not-a-diet"
          >
            <p className="font-serif italic text-2xl md:text-3xl text-[#efeae7] leading-tight mb-3">
              This is hygiene, <span className="text-[#d4af6a]">not a diet.</span>
            </p>
            <p className="font-sans text-[11px] tracking-[0.32em] uppercase text-[#d4af6a]/85 font-semibold">
              Nourishing. Never shrinking.
            </p>
          </div>

          <p className="mb-7">
            Hear me on this. Body hygiene is about <em>nourishing</em> yourself, never shrinking yourself. There are no forbidden foods here, no earning your meals, no rules. The only question is gentle: <em>Have I cared for this body today?</em>
          </p>

          <p className="mb-7">
            And if eating itself feels hard right now — if food carries a weight a checklist can&rsquo;t hold — that&rsquo;s not a failing, and you don&rsquo;t have to white-knuckle it alone. That deserves real, kind support. Please reach for it.
          </p>

          {/* Section divider */}
          <div className="my-16 flex items-center justify-center gap-4" aria-hidden="true">
            <div className="h-px w-16 bg-[#d4af6a]/40" />
            <span className="text-[#d4af6a]">✦</span>
            <div className="h-px w-16 bg-[#d4af6a]/40" />
          </div>

          <h2 className="font-serif italic text-3xl md:text-4xl text-[#042F2E] text-center leading-tight mb-10" data-testid="section-practice">
            Your Practice
          </h2>

          <p className="mb-7">
            Tomorrow morning, before the world comes for you, run the five:
          </p>

          <div className="my-10 py-8 px-6 text-center bg-[#068fa2]/8 rounded-2xl border border-[#068fa2]/15">
            <p className="font-serif italic text-2xl md:text-3xl text-[#068fa2] leading-relaxed">
              Fed?<br />
              Watered?<br />
              Rested?<br />
              Moved?<br />
              Freshened?
            </p>
          </div>

          <p className="mb-7">
            Whatever&rsquo;s missing, give yourself that one thing first. Watch how much kinder you are to everyone — including you — when your tank isn&rsquo;t empty.
          </p>

          {/* Section divider */}
          <div className="my-16 flex items-center justify-center gap-4" aria-hidden="true">
            <div className="h-px w-16 bg-[#d4af6a]/40" />
            <span className="text-[#d4af6a]">✦</span>
            <div className="h-px w-16 bg-[#d4af6a]/40" />
          </div>

          <p className="mb-7 text-center font-serif italic text-xl text-[#1E2A29]">
            You feed the people you love without a second thought.<br />
            Today, dear one — you&rsquo;re one of them.
          </p>

          <p className="mb-7 text-center font-serif italic text-2xl text-[#068fa2]">
            Welcome home.
          </p>

          <p className="mb-12 text-center font-sans text-sm tracking-widest uppercase text-[#5F7370]">
            — Temple
          </p>
        </div>
      </section>

      {/* THE TWO DOORS — Body Check-In printable + Workbook */}
      <section className="px-6 pb-24" data-testid="blog-doors">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="font-serif italic text-2xl md:text-3xl text-[#1E2A29]" data-testid="doors-eyebrow">
              Want it where you can see it?
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Door 1 — The Body Check-In printable */}
            <a
              href="/printables/Body_CheckIn_Poster.pdf"
              download="Daily-Check-In-Poster.pdf"
              data-testid="door-checkin-printable"
              className="group block bg-[#042F2E] rounded-3xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="text-3xl mb-4" aria-hidden="true">📋</div>
              <h3 className="font-serif italic text-2xl text-[#efeae7] mb-3 leading-tight">
                The free Daily Check-In poster.
              </h3>
              <p className="font-serif text-[15px] text-[#efeae7]/80 leading-relaxed mb-5">
                Mind · Body · Spirit, all on one page. Print it. Put it on the fridge or the mirror. Run the five before the world comes for you.
              </p>
              <p className="font-sans text-xs tracking-[0.28em] uppercase text-[#d4af6a] font-semibold group-hover:underline underline-offset-4">
                Download PDF →
              </p>
            </a>

            {/* Door 2 — The Workbook */}
            <Link
              to="/workbook?from=blog-body"
              data-testid="door-workbook"
              className="group block bg-white rounded-3xl p-8 shadow-sm border border-[#042F2E]/8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="text-3xl mb-4" aria-hidden="true">📖</div>
              <h3 className="font-serif italic text-2xl text-[#042F2E] mb-3 leading-tight">
                The free I GET TO workbook.
              </h3>
              <p className="font-serif text-[15px] text-[#1E2A29] leading-relaxed mb-5">
                12 pages. The simple tool that helps you rewire how you talk to yourself, one true word at a time.
              </p>
              <p className="font-sans text-xs tracking-[0.28em] uppercase text-[#068fa2] font-semibold group-hover:underline underline-offset-4">
                Get the workbook →
              </p>
            </Link>
          </div>

          {/* Continue reading — link to sibling Messy Middle post */}
          <div className="mt-16 text-center" data-testid="continue-reading">
            <p className="font-sans text-[11px] tracking-[0.32em] uppercase text-[#5F7370] font-medium mb-3">
              Keep reading
            </p>
            <Link
              to="/blog/the-messy-middle-is-the-door"
              className="font-serif italic text-xl md:text-2xl text-[#068fa2] hover:text-[#042F2E] transition-colors underline decoration-[#d4af6a]/40 decoration-2 underline-offset-4"
              data-testid="link-messy-middle"
            >
              The Messy Middle Is the Door →
            </Link>
          </div>

          {/* Calm calendar opt-in — quiet invitation after the benediction */}
          <div className="mt-12 max-w-2xl mx-auto">
            <BlogCalendarOptin articleSlug="you-feed-the-baby" />
          </div>
        </div>
      </section>
    </article>
  );
};

export default BlogYouFeedTheBaby;
